<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store.js';
import { useAlert } from 'dashboard/composables';
import EnterpriseAccountAPI from 'dashboard/api/enterprise/account';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const { t } = useI18n();
const store = useStore();

const uiFlags = useMapGetter('integrations/getUIFlags');
const isOpeningPortal = ref(false);

const lagoIntegration = computed(() => {
  return store.getters['integrations/getIntegration']('lago', {});
});

const portalUrl = computed(() => {
  const hooks = lagoIntegration.value?.hooks;
  if (hooks && hooks.length > 0) {
    return hooks[0]?.settings?.portal_url || '';
  }
  return '';
});

const isLoading = computed(() => uiFlags.value.isFetching);

const openPortal = async () => {
  const portalWindow = window.open('', '_blank');
  isOpeningPortal.value = true;

  try {
    const response = await EnterpriseAccountAPI.checkout();
    const redirectUrl = response.data?.redirect_url;

    if (!redirectUrl) {
      throw new Error('The Lago customer portal URL is not available yet.');
    }

    if (portalWindow) {
      portalWindow.opener = null;
      portalWindow.location.href = redirectUrl;
    } else {
      const openedWindow = window.open(redirectUrl, '_blank', 'noopener,noreferrer');
      if (!openedWindow) {
        window.location.assign(redirectUrl);
      }
    }
  } catch (error) {
    portalWindow?.close();
    useAlert(error.response?.data?.error || error.message);
  } finally {
    isOpeningPortal.value = false;
  }
};

onMounted(() => {
  store.dispatch('integrations/get');
});
</script>

<template>
  <div class="flex flex-col h-full w-full bg-n-background">
    <div v-if="isLoading" class="flex items-center justify-center flex-1">
      <Spinner class="text-n-brand" />
    </div>
    <div
      v-else-if="!portalUrl"
      class="flex flex-col items-center justify-center flex-1 gap-2"
    >
      <p class="text-n-slate-11 text-sm">
        {{ t('BILLING_REPORTS.NOT_CONFIGURED') }}
      </p>
    </div>
    <div v-else class="flex flex-1 items-center justify-center p-6">
      <div class="max-w-md rounded-xl border border-n-weak bg-n-solid-1 p-8 text-center shadow-sm">
        <h2 class="mb-2 text-lg font-semibold text-n-slate-12">
          {{ t('BILLING_REPORTS.HEADER') }}
        </h2>
        <p class="mb-6 text-sm text-n-slate-11">
          {{ t('BILLING_REPORTS.PORTAL_DESCRIPTION') }}
        </p>
          <button
            type="button"
            class="rounded-lg bg-n-brand px-4 py-2 text-sm font-medium text-white transition hover:opacity-90"
            :disabled="isOpeningPortal"
            @click="openPortal"
        >
          {{ t('BILLING_REPORTS.OPEN_PORTAL') }}
        </button>
      </div>
    </div>
  </div>
</template>
